import { createFileRoute, useSearch } from '@tanstack/react-router';
import { ForwardReturnsWidget } from '~/components/ui/widgets/ForwardReturnsWidget/ForwardReturnsWidget';
import { useQuery } from '@apollo/client';
import { useState, useEffect, useCallback } from 'react';
import { endpointPortfolioForwardProjectionGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import {
  GET_PORTFOLIO_NAME,
  GET_LATEST_SNAPSHOT_ID,
} from '~/graphql/queries/portfolio';
import { useHasBenchmark } from '~/hooks/useHasBenchmark';

type SearchParams = {
  portfolioId?: string;
  snapshotId?: string;
};

export const Route = createFileRoute('/_authLayout/debug/forward-returns')({
  component: DebugForwardReturns,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      portfolioId: search.portfolioId as string | undefined,
      snapshotId: search.snapshotId as string | undefined,
    };
  },
});

function DebugForwardReturns() {
  const { portfolioId, snapshotId } = useSearch({
    from: '/_authLayout/debug/forward-returns',
  });
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [actualSnapshotId, setActualSnapshotId] = useState<string | null>(null);

  // Check if portfolio has a benchmark using the dedicated endpoint
  const { hasBenchmark, isLoading: hasBenchmarkLoading } =
    useHasBenchmark(portfolioId);

  // Get latest snapshot ID
  const { data: snapshotData } = useQuery(GET_LATEST_SNAPSHOT_ID, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Get portfolio info (optional, for benchmark)
  const { data: portfolioData } = useQuery(GET_PORTFOLIO_NAME, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  // Fetch projection data
  const fetchData = useCallback(async () => {
    if (!snapshotId && !portfolioId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Get latest snapshot if not provided
      let targetSnapshotId = snapshotId;
      if (
        !targetSnapshotId &&
        snapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id
      ) {
        targetSnapshotId =
          snapshotData.EQPortfolio[0].EQPortfolioSnapshots[0].id;
      }

      if (!targetSnapshotId) {
        throw new Error('No snapshot ID available');
      }

      setActualSnapshotId(targetSnapshotId);

      const response = await endpointPortfolioForwardProjectionGET({
        snapshot_id: targetSnapshotId,
        horizon_days: 252,
        include_current: true,
        include_benchmark: hasBenchmark,
      });

      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [snapshotId, portfolioId, snapshotData, hasBenchmark]);

  useEffect(() => {
    // Wait for benchmark check to complete before fetching
    if (hasBenchmarkLoading) {
      return;
    }

    if (portfolioId) {
      fetchData();
    }
  }, [portfolioId, fetchData, hasBenchmarkLoading]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ color: 'white', marginBottom: '20px' }}>
        Forward Returns Widget - Debug Page
      </h1>

      {!portfolioId ? (
        <div style={{ color: '#888' }}>
          <p>Add query parameters to load real data:</p>
          <p>?portfolioId=YOUR_PORTFOLIO_ID&snapshotId=YOUR_SNAPSHOT_ID</p>
          <p>Example: ?portfolioId=0199e3bb-61d5-7825-96b3-6d2cc0d96a9c</p>
        </div>
      ) : (
        <>
          <p style={{ color: '#888', marginBottom: '20px' }}>
            Portfolio ID: {portfolioId}
            <br />
            Snapshot ID: {snapshotId || 'latest'}
            <br />
            Has Benchmark: {hasBenchmark ? 'Yes' : 'No'}
          </p>

          <div
            style={{
              height: '600px',
              backgroundColor: '#2a2a2a',
              padding: '20px',
            }}
          >
            <ForwardReturnsWidget
              data={data || null}
              isLoading={isLoading || hasBenchmarkLoading}
              error={error}
              refetch={refetch}
              widgetId="debug-forward-returns"
              title="Debug Forward Returns"
              snapshotId={actualSnapshotId}
              benchmarkName="Benchmark"
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: '20px',
                color: 'red',
                padding: '20px',
                backgroundColor: '#3a2a2a',
              }}
            >
              <h3>Error:</h3>
              <pre>{error.message}</pre>
              <pre>{error.stack}</pre>
            </div>
          )}

          {data && (
            <div style={{ marginTop: '20px', color: 'white' }}>
              <h3>Batch Dates Found:</h3>
              <ul>
                <li>
                  Current Portfolio:{' '}
                  {data.current_batch_dates?.join(', ') || 'None'}
                </li>
                <li>
                  Proposed Portfolio:{' '}
                  {data.proposed_batch_dates?.join(', ') || 'None'}
                </li>
              </ul>
              <h3>Data Points:</h3>
              <p>Total dates: {data.dates?.length || 0}</p>
              <p>Today index: {data.today_index}</p>
              <h3>Confidence Bounds:</h3>
              <p>
                Has proposed_portfolio_lower:{' '}
                {data.proposed_portfolio_lower ? 'Yes' : 'No'}
              </p>
              <p>
                Has proposed_portfolio_upper:{' '}
                {data.proposed_portfolio_upper ? 'Yes' : 'No'}
              </p>
              {data.proposed_portfolio_lower && (
                <p>
                  Sample lower bounds (first 5):{' '}
                  {data.proposed_portfolio_lower
                    .slice(0, 5)
                    .map((v: number) => v?.toFixed(2))
                    .join(', ')}
                </p>
              )}
              {data.proposed_portfolio_upper && (
                <p>
                  Sample upper bounds (first 5):{' '}
                  {data.proposed_portfolio_upper
                    .slice(0, 5)
                    .map((v: number) => v?.toFixed(2))
                    .join(', ')}
                </p>
              )}
            </div>
          )}

          {isLoading && (
            <div style={{ marginTop: '20px', color: 'yellow' }}>
              <p>Loading...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
